// services/taskService.ts
import axios from "axios";
import {
  Task,
  TaskCreatePayload,
  TaskUpdatePayload,
  TaskPriority,
  TaskStatus,
  Agent,
} from "../../types/features/support/Task";
import { PaginatedResponse } from "../../types/utils/PaginatedResponse";
import { MAIN_API_URL } from "../../config/api";

export interface TaskFilters {
  status?: TaskStatus;
  priority?: TaskPriority;
  assigned_to?: string;
  created_by?: string;
  assigned_to_me?: boolean;
  created_by_me?: boolean;
  due_before?: string;
  due_after?: string;
  search?: string;
  client_id?: string;
}

export interface TaskStats {
  total: number;
  nouveau: number;
  en_cours: number;
  en_attente: number;
  resolu: number;
  overdue: number;
  assigned_to_me: number;
  created_by_me: number;
}

/** Type de réponse standard du backend : { data: T } */
interface DataResponse<T> {
  data: T;
}

export const taskService = {
  // ============================================
  // 📋 LISTE & DÉTAIL
  // ============================================

  async getTasks(
    page: number = 1,
    perPage: number = 20,
    filters?: TaskFilters,
    token?: string
  ): Promise<PaginatedResponse<Task>> {
    const response = await axios.get<PaginatedResponse<Task>>(`${MAIN_API_URL}/tasks`, {
      params: { page, per_page: perPage, ...filters },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data; // ✅ paginator direct
  },

  async getTaskById(id: string, token?: string): Promise<Task | null> {
    const response = await axios.get<DataResponse<Task>>(`${MAIN_API_URL}/tasks/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data; // ✅
  },

  async getTasksByClient(
    clientId: string,
    page: number = 1,
    perPage: number = 20,
    token?: string
  ): Promise<PaginatedResponse<Task>> {
    const response = await axios.get<PaginatedResponse<Task>>(`${MAIN_API_URL}/tasks`, {
      params: { client_id: clientId, page, per_page: perPage },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  // ============================================
  // ✏️ CRÉATION & MISE À JOUR
  // ============================================

  async createTask(payload: TaskCreatePayload, token?: string): Promise<Task> {
    const response = await axios.post<DataResponse<Task>>(`${MAIN_API_URL}/tasks`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data; // ✅
  },

  updateNote: async (id: string, note: string | null, token?: string) => {
    const { data } = await axios.patch(
      `/tasks/${id}/note`,
      { note },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data.data;
  },
  async updateTask(id: string, data: TaskUpdatePayload, token?: string): Promise<Task> {
    const response = await axios.put<DataResponse<Task>>(`${MAIN_API_URL}/tasks/${id}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data; // ✅
  },

  async updateStatus(id: string, status: TaskStatus, token?: string): Promise<Task> {
    const response = await axios.patch<DataResponse<Task>>(
      `${MAIN_API_URL}/tasks/${id}/status`,
      { status },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.data; // ✅
  },

  async updatePriority(id: string, priority: TaskPriority, token?: string): Promise<Task> {
    const response = await axios.patch<DataResponse<Task>>(
      `${MAIN_API_URL}/tasks/${id}/priority`,
      { priority },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.data; // ✅
  },

  // ============================================
  // 👥 ASSIGNATION
  // ============================================

  async assignAgents(taskId: string, agentIds: string[], token?: string): Promise<Task> {
    const response = await axios.post<DataResponse<Task>>(
      `${MAIN_API_URL}/tasks/${taskId}/assign`,
      { agents: agentIds },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.data; // ✅
  },

  async unassignAgent(taskId: string, agentId: string, token?: string): Promise<Task> {
    const response = await axios.delete<DataResponse<Task>>(`${MAIN_API_URL}/tasks/${taskId}/assign/${agentId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data; // ✅
  },

  // ============================================
  // 📎 PIÈCES JOINTES
  // ============================================

  async addAttachments(taskId: string, attachments: string[], token?: string): Promise<Task> {
    const response = await axios.post<DataResponse<Task>>(
      `${MAIN_API_URL}/tasks/${taskId}/attachments`,
      { attachments },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.data; // ✅
  },

  async removeAttachment(taskId: string, attachment: string, token?: string): Promise<Task> {
    const response = await axios.delete<DataResponse<Task>>(`${MAIN_API_URL}/tasks/${taskId}/attachments`, {
      data: { attachment },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data; // ✅
  },

  // ============================================
  // ✅ RÉSOLUTION & SUPPRESSION
  // ============================================

  async resolveTask(taskId: string, token?: string): Promise<Task> {
    const response = await axios.post<DataResponse<Task>>(
      `${MAIN_API_URL}/tasks/${taskId}/resolve`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.data; // ✅
  },

  async holdTask(taskId: string, token?: string): Promise<Task> {
    const response = await axios.post<DataResponse<Task>>(
      `${MAIN_API_URL}/tasks/${taskId}/hold`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.data; // ✅
  },

  async deleteTask(id: string, token?: string): Promise<void> {
    await axios.delete(`${MAIN_API_URL}/tasks/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  },

  // ============================================
  // 📊 STATISTIQUES & COMPTEURS
  // ============================================

  async getStats(token?: string, userId?: string): Promise<TaskStats> {
    const response = await axios.get<DataResponse<TaskStats>>(`${MAIN_API_URL}/tasks/stats`, {
      params: userId ? { user_id: userId } : {},
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data; // ✅
  },

  async getMyPendingCount(token?: string, userId?: string): Promise<number> {
    const response = await axios.get<{ data: { count: number } }>(`${MAIN_API_URL}/tasks/my-pending-count`, {
      params: userId ? { user_id: userId } : {},
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data?.data?.count ?? 0; // ✅ déjà bon
  },

  async getUnreadCount(token?: string, userId?: string): Promise<number> {
    const response = await axios.get<{ data: { count: number } }>(`${MAIN_API_URL}/tasks/unread-count`, {
      params: userId ? { user_id: userId } : {},
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data?.data?.count ?? 0; // ✅ déjà bon
  },

  // ============================================
  // 👤 ADMINS
  // ============================================

  async getAdmins(token?: string): Promise<Agent[]> {
    const response = await axios.get<DataResponse<Agent[]>>(`${MAIN_API_URL}/users`, {
      params: { role: "admin" },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data; // ⚠️ à vérifier selon ton UserController
  },
};
